import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import './LandingPage.css';
import {
  ArrowRight,
  BarChart3,
  MousePointer,
  Zap,
  Shield,
  MessageSquare,
  Map,
  Smartphone,
  AlertCircle,
  ShoppingBag,
  Store,
  Globe,
  X,
} from 'lucide-react';

const FEATURES = [
  {
    key: 'socialListening',
    icon: <MessageSquare className="feature-icon" />,
    title: 'Social Listening',
    description: 'Monitor what customers say about you on Reddit, Twitter, and review sites—connect complaints to on-site friction.',
    popup: {
      title: "Social Listening → Hear What They Won't Tell You",
      body: `Your visitors won't tell you what's broken. But they'll tell everyone else. BaoBrain monitors Reddit, Twitter, forums, and review sites to surface customer complaints and frustrations.

Why it matters: Most analytics tools only show what happens on your site. Social listening reveals the "why"—connecting external feedback to internal friction points so you know exactly what to fix.`
    }
  },
  {
    key: 'rageClick',
    icon: <MousePointer className="feature-icon" />,
    title: 'Click Error Detection',
    description: 'Identify frustrating elements that make customers repeatedly click. Fix UX issues before you lose sales.',
    popup: {
      title: 'Rage Click Detection → Catch Frustration Early',
      body: `When someone frantically clicks the same spot, it's a red-flag: they're frustrated, confused, or about to leave. Rage Click Detection automatically spots these moments.

Why it matters: By pinpointing exactly where customers vent their frustration, you can eliminate UX pain-points before they cost you sales—and show shoppers you care about a smooth experience.`
    }
  },
  {
    key: 'conversionAnalytics',
    icon: <BarChart3 className="feature-icon" />,
    title: 'Conversion Analytics',
    description: 'Track your funnel performance and discover exactly where potential customers drop off.',
    popup: {
      title: 'Conversion Analytics → Optimize Your Funnel',
      body: `Track every step of your checkout funnel—from landing page to thank-you screen—and see exactly where drop-off happens.

Why it matters: Knowing that 40% of users abandon at shipping choices, or 20% at payment, lets you zero in on the pages that need tweaking—so you can turn more browsers into buyers and boost revenue.`
    }
  },
  {
    key: 'aiInsights',
    icon: <Zap className="feature-icon" />,
    title: 'AI-Powered Insights',
    description: "Get intelligent recommendations to improve your store's performance automatically.",
    popup: {
      title: 'AI-Powered Insights → Data-Driven Recommendations',
      body: `BaoBrain's AI doesn't just collect data—it analyzes patterns and surface high-impact fixes, like 'Your add-to-cart button is too low on mobile' or 'Reduce form fields to improve sign-ups.'
Why it matters: You get clear, prioritized to-dos powered by machine learning—no data science degree required—to improve your site faster than guessing or gut-feel ever could.`
    }
  },
  {
    key: 'behavioralHeatmaps',
    icon: <Map className="feature-icon" />,
    title: 'Behavioral Heatmaps',
    description: 'Visualize where customers click, scroll, and hover on each page—spot attention hotspots and blind spots at a glance.',
    popup: {
      title: 'Behavioral Heatmaps → Visualize Attention',
      body: `See where customers click, scroll, and hover on each page—hotspots light up in vibrant color.

Why it matters: Heatmaps reveal what draws your visitors' eyes (and what they ignore). Use this insight to place CTAs, promotions, and key content exactly where they'll be seen—and skyrocket engagement.`
    }
  },
  {
    key: 'deviceBrowserInsights',
    icon: <Smartphone className="feature-icon" />,
    title: 'Device & Browser Insights',
    description: 'Compare user behavior and performance across desktop, mobile, and tablet. Optimize your site for every device to keep shoppers engaged everywhere.',
    popup: {
      title: 'Device & Browser Insights → Optimize Across Devices',
      body: `Desktop, mobile, tablet—each device shows unique behavior patterns. BaoBrain breaks down how users interact on every screen.

Why it matters: A page that works on desktop might break on mobile. By comparing performance across devices, you can fix mobile drop-offs, ensure fast load times, and deliver a consistent experience that keeps shoppers moving toward checkout.`
    }
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  const [modalActive, setModalActive] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const [platformModalActive, setPlatformModalActive] = useState(false);

  const handleOpenModal = useCallback((feature) => {
    setActiveFeature(feature);
    setModalActive(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setActiveFeature(null);
    setModalActive(false);
  }, []);

  const benefits = [
    { icon: <MousePointer />, text: 'Catch friction before it costs you sales' },
    { icon: <MessageSquare />, text: 'Hear what customers say off your site' },
    { icon: <Zap />, text: 'AI-powered fix list, no dashboards' },
    { icon: <Shield />, text: 'GDPR compliant & privacy-first' },
  ];

  const painPoints = [
    {
      icon: <AlertCircle className="pain-point-icon" />,
      title: 'Confusing Navigation',
      description: "Visitors can't find what they want in seconds—and they leave."
    },
    {
      icon: <AlertCircle className="pain-point-icon" />,
      title: 'Unclear Calls-to-Action',
      description: 'If your "Buy" button hides or blends in, customers hesitate and bounce.'
    },
    {
      icon: <AlertCircle className="pain-point-icon" />,
      title: 'Slow or Buggy Pages',
      description: 'Every extra second of load time or glitch drives shoppers away.'
    },
    {
      icon: <AlertCircle className="pain-point-icon" />,
      title: 'No Visibility Into Behavior',
      description: "You don't know why people drop off, so you can't fix the real problem."
    },
  ];

  // Load Wistia player scripts once on mount
  useEffect(() => {
    const scripts = [
      { id: 'wistia-player', src: 'https://fast.wistia.com/player.js' },
      { id: 'wistia-media-y34y4z0yts', src: 'https://fast.wistia.com/embed/y34y4z0yts.js', module: true },
    ];

    scripts.forEach(({ id, src, module }) => {
      if (document.getElementById(id)) return;
      const s = document.createElement('script');
      s.id = id;
      s.src = src;
      s.async = true;
      if (module) s.type = 'module';
      document.body.appendChild(s);
    });
  }, []);

  return (
    <Layout hideSidebar>
      <div className="landing-page">
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-container">
            <div className="hero-content">
              <div className="hero-badge">
                <Zap size={16} />
                <span>Friction Detection + Social Listening + AI Insights</span>
              </div>

              <h1 className="hero-title">
                Stop Guessing Why Your{' '}
                <span className="text-gradient">Traffic Doesn't Convert</span>
              </h1>

              <p className="hero-description">
                BaoBrain combines behavioral analytics, social listening, and AI to pinpoint exactly where you're losing sales—then tells you what to fix first.
              </p>

              <div className="hero-actions-container">
                <div className="hero-actions">
                <button className="btn btn-primary hero-btn-glow" onClick={() => setPlatformModalActive(true)}>
  Get Started Free <ArrowRight size={20} />
</button>
                </div>
              </div>
            </div>

            <div className="hero-visual">
              <div className="video-container">
                <wistia-player
                  media-id="y34y4z0yts"
                  aspect="1.7777777777777777"
                  className="dashboard-video"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Pain Point Section */}
        <section className="pain-points">
          <div className="container pain-points-grid">
            {/* LEFT COLUMN: headline + description */}
            <div className="pain-points-intro">
              <h2>
                Did you know? <br />
                Every Frustration Is a Lost Sale
              </h2>
              <p>
                Most stores lose 60-80% of visitors before checkout—and never know why. 
                BaoBrain shows you exactly where people get stuck: confusing navigation, 
                hidden buttons, slow pages, or unclear CTAs. Then tells you what to fix first.
              </p>
            </div>

            {/* RIGHT COLUMN: the list */}
            <div className="pain-points-list">
              {painPoints.map((point, index) => (
                <div key={index} className="pain-point-item">
                  <div className="pain-point-icon-wrapper">{point.icon}</div>
                  <div className="pain-point-text">
                    <h3>{point.title}</h3>
                    <p>{point.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* New Thought-Leadership Hook Section */}
        <section className="thought-leadership">
          <div className="container">
            <div className="section-header">
              <h2>Data-Driven Decisions Aren't Optional</h2>
              <p className="centered-text-p">
                Running an online store is hard enough—you shouldn't have to guess why customers leave. BaoBrain shows you exactly where they get stuck, so you can make informed changes that truly improve your store.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works">
          <div className="container">
            <div className="section-header">
              <h2>How BaoBrain Works</h2>
              <p>Three layers of intelligence to pinpoint what's blocking sales</p>
            </div>

            <div className="how-it-works-grid">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>Detect Friction</h3>
                <p>Track rage clicks, dead clicks, form errors, and page drop-offs in real-time.</p>
              </div>

              <div className="step-card">
                <div className="step-number">2</div>
                <h3>Listen Externally</h3>
                <p>Monitor social media mentions, Reddit threads, and review sentiment—see what customers say when you're not listening.</p>
              </div>

              <div className="step-card">
                <div className="step-number">3</div>
                <h3>Get Recommendations</h3>
                <p>AI-powered fix list, prioritized by revenue impact. No dashboards, just action.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features">
          <div className="container">
            <div className="section-header">
              <h2>How we can help your business</h2>
              <p>Get deep insights into user behavior with our comprehensive analytics suite</p>
            </div>

            <div className="features-grid">
              {FEATURES.map((feature) => (
                <div
                  key={feature.key}
                  className="feature-card"
                  onClick={() => handleOpenModal(feature)}
                >
                  <div className="feature-icon-wrapper">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="benefits">
          <div className="container">
            <div className="benefits-content">
              <div className="benefits-text">
                <h2>Why stores choose BaoBrain</h2>
                <p>
                  Don't lose customers to hidden friction points. Our platform helps
                  you identify and fix issues that are costing you sales every day.
                </p>

                <div className="benefits-list">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="benefit-item">
                      <div className="benefit-icon">{benefit.icon}</div>
                      <span>{benefit.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Demo Section */}
        <section className="demo-section">
          <div className="container">
            <div className="section-header">
              <h2>See how easy setup is</h2>
              <p>Click through the interactive walkthrough — takes 2 minutes to install on any platform</p>
            </div>
            <div className="demo-embed-wrapper">
              <div className="demo-embed-container">
                <iframe
                  src="https://app.supademo.com/embed/cmnrz5ecd31e0cr4ji28lrndr"
                  title="BaoBrain Setup Walkthrough"
                  frameBorder="0"
                  allowFullScreen
                  allow="clipboard-write"
                  loading="lazy"
                />
              </div>
              <p className="demo-caption">
                Works on Shopify, BigCommerce, and WooCommerce — same simple setup
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to see what's blocking sales?</h2>
              <p>
                Get started in minutes. Connect your store and get your first friction report within 24 hours.
              </p>

              <div className="cta-actions">
                <button className="btn btn-primary btn-large" onClick={() => setPlatformModalActive(true)}>
                  Get Started Free <ArrowRight size={24} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <div className="footer-brand">
                <h3>BaoBrain</h3>
                <p>Analytics that grow your business</p>
              </div>

              <div className="footer-links">
                <a href="https://app.baobrain.com/installation" className="footer-link">
                  Installation Guide
                </a>
                <a href="https://app.baobrain.com/privacyPolicy" className="footer-link">
                  Privacy Policy
                </a>
                <a href="https://app.baobrain.com/terms" className="footer-link">
                  Terms of Service
                </a>
                <a href="https://app.baobrain.com/contact" className="footer-link">
                  Contact us
                </a>
              </div>
            </div>

            <div className="footer-bottom">
              <p>© 2026 BaoBrain. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>

      {/* Platform Selection Modal */}
      {platformModalActive && (
        <div className="modal-overlay" onClick={() => setPlatformModalActive(false)}>
          <div className="platform-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-button" onClick={() => setPlatformModalActive(false)}>
              <X size={24} />
            </button>
            
            <div className="platform-modal-header">
              <h2>Choose Your Platform</h2>
              <p>Connect your store in seconds</p>
            </div>
            
            <div className="platform-tiles">
              <a 
                href="https://apps.shopify.com/baobrain-connector" 
                className="platform-tile"
              >
                <div className="platform-icon shopify">
                  <ShoppingBag size={32} />
                </div>
                <h3>Shopify</h3>
                <p>One-click install from the Shopify App Store</p>
                <span className="platform-cta">
                  Install Now <ArrowRight size={16} />
                </span>
              </a>
              
              <a 
                href="https://www.bigcommerce.com/apps/baobrain/" 
                className="platform-tile"
              >
                <div className="platform-icon bigcommerce">
                  <Store size={32} />
                </div>
                <h3>BigCommerce</h3>
                <p>One-click install from the BigCommerce Marketplace</p>
                <span className="platform-cta">
                  Install Now <ArrowRight size={16} />
                </span>
              </a>

              <a 
                href="https://wordpress.org/plugins/baobrain-analytics-for-woocommerce/" 
                className="platform-tile"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="platform-icon woocommerce">
                  <ShoppingBag size={32} />
                </div>
                <h3>WooCommerce</h3>
                <p>Free plugin from WordPress.org</p>
                <span className="platform-cta">
                  Install Now <ArrowRight size={16} />
                </span>
              </a>
              
              <div 
                className="platform-tile"
                onClick={() => {
                  setPlatformModalActive(false);
                  navigate('/signup');
                }}
              >
                <div className="platform-icon other">
                  <Globe size={32} />
                </div>
                <h3>Other Platforms</h3>
                <p>Custom stores & more</p>
                <span className="platform-cta">
                  Sign Up <ArrowRight size={16} />
                </span>
              </div>
            </div>
            
            <div className="platform-modal-footer">
              <p>Already have an account? <button className="link-button" onClick={() => { setPlatformModalActive(false); navigate('/login'); }}>Log in</button></p>
            </div>
          </div>
        </div>
      )}

      {modalActive && activeFeature && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{activeFeature.popup.title}</h2>
              <button className="modal-close-button" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              {activeFeature.popup.body.split('\n\n').map((para, index) => (
                <p key={index}>{para}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}